/**
 * TodayScreen
 * Main screen for marking today's goals as complete
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  SafeAreaView,
  Platform,
} from 'react-native';
import { TodayScreenProps } from '../navigation/types';
import { useGoals } from '../hooks/useGoals';
import { useCompletions } from '../hooks/useCompletions';
import { useEncouragement } from '../hooks/useEncouragement';
import { GoalCard } from '../components/GoalCard';
import { EncouragementModal } from '../components/EncouragementModal';
import { formatDateForDisplay, getTodayDateString } from '../utils/dateUtils';
import { performanceMonitor } from '../utils/PerformanceMonitor';

export const TodayScreen: React.FC<TodayScreenProps> = ({ navigation }) => {
  const {
    mainGoal,
    sideGoals,
    currentAccount,
    currentAccountId,
    isLoading: goalsLoading,
    canSwitchAccount,
    refreshGoals,
  } = useGoals();

  const {
    completions,
    isCompletedToday,
    toggleToday,
    loadRecentCompletions,
    getCompletionSummary,
    getRecentCompletions,
    isLoading: completionsLoading,
  } = useCompletions();

  const {
    currentMessage,
    checkForEncouragement,
    dismissMessage,
    submitFeedback,
    loadMessageHistory,
  } = useEncouragement();

  const [showSideGoals, setShowSideGoals] = useState(true);
  const [previousStreak, setPreviousStreak] = useState(0);
  const today = getTodayDateString();
  const todayDisplay = formatDateForDisplay(today);

  // Get theme color from account or use default
  const themeColor = currentAccount?.themeColor || '#007AFF';

  // Use refs to store latest values for focus listener
  const latestDataRef = useRef({ currentAccountId, mainGoal, sideGoals, refreshGoals, loadRecentCompletions });
  latestDataRef.current = { currentAccountId, mainGoal, sideGoals, refreshGoals, loadRecentCompletions };

  // Memoized function to reload all completions
  const reloadAllCompletions = useCallback(() => {
    const { currentAccountId, mainGoal, sideGoals, refreshGoals, loadRecentCompletions } = latestDataRef.current;
    console.log('[TodayScreen] Screen focused - refreshing data');
    refreshGoals();

    // Reload completions for all goals to ensure fresh data
    if (currentAccountId && mainGoal) {
      console.log('[TodayScreen] Loading completions for main goal:', mainGoal.id);
      loadRecentCompletions(currentAccountId, mainGoal.id, 365);
    }
    if (currentAccountId && sideGoals.length > 0) {
      console.log('[TodayScreen] Loading completions for', sideGoals.length, 'side goals');
      sideGoals.forEach(goal => {
        loadRecentCompletions(currentAccountId, goal.id, 365);
      });
    }
  }, []);

  // Refresh goals and completions when screen comes into focus (after adding daily actions or navigating back)
  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', reloadAllCompletions);
    return unsubscribe;
  }, [navigation, reloadAllCompletions]);

  // Load recent completions on mount (365 days for accurate streak calculation)
  useEffect(() => {
    if (currentAccountId && mainGoal) {
      console.log('[TodayScreen] Initial load - loading completions for main goal:', mainGoal.id);
      loadRecentCompletions(currentAccountId, mainGoal.id, 365);
    }

    // Load encouragement message history
    if (currentAccountId) {
      loadMessageHistory(currentAccountId);
    }
  }, [currentAccountId, mainGoal?.id]);

  // Load side goals completions separately to track individual goal changes
  useEffect(() => {
    if (currentAccountId && sideGoals.length > 0) {
      console.log('[TodayScreen] Loading completions for', sideGoals.length, 'side goals');
      sideGoals.forEach(goal => {
        loadRecentCompletions(currentAccountId, goal.id, 365);
      });
    }
  }, [currentAccountId, sideGoals.map(g => g.id).join(',')]);

  const handleToggleMainGoal = async () => {
    if (!currentAccountId || !mainGoal) return;

    // Store current streak before toggle
    const summaryBefore = getCompletionSummary(mainGoal.id);
    setPreviousStreak(summaryBefore.currentStreak);

    await performanceMonitor.measureTapLatency('toggle_main_goal', async () => {
      await toggleToday(currentAccountId, mainGoal.id);
    });

    // Check if goal was marked complete (not uncomplete)
    const isNowCompleted = isCompletedToday(mainGoal.id);
    console.log('Goal toggled:', { isNowCompleted, previousStreak: summaryBefore.currentStreak });

    if (isNowCompleted) {
      // Get recent completions and check for encouragement patterns
      const completions = getRecentCompletions(mainGoal.id, 365);
      console.log('Checking for encouragement with completions:', completions.length);
      await checkForEncouragement(currentAccountId, completions, summaryBefore.currentStreak);
    }
  };

  const handleToggleSideGoal = async (goalId: string) => {
    if (!currentAccountId) return;

    performanceMonitor.measureTapLatency('toggle_side_goal', async () => {
      await toggleToday(currentAccountId, goalId);
    });
  };

  if (goalsLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={themeColor} />
          <Text style={styles.loadingText}>Loading your goals...</Text>
        </View>
      </SafeAreaView>
    );
  }

  // Show empty state if no main goal
  if (!mainGoal) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyTitle}>Welcome to EverydayGoal!</Text>
          <Text style={styles.emptyText}>
            You don't have a main goal yet. Let's create one to get started.
          </Text>
          <TouchableOpacity
            style={[styles.emptyButton, { backgroundColor: themeColor }]}
            onPress={() => navigation.navigate('CreateMainGoal')}
          >
            <Text style={styles.emptyButtonText}>Create Main Goal</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  const mainGoalSummary = getCompletionSummary(mainGoal.id);
  const mainGoalCompleted = isCompletedToday(mainGoal.id);

  // Check if we have loaded any completions for the main goal yet
  const hasLoadedCompletions = completions.get(mainGoal.id) !== undefined;

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerInfo}>
            <Text style={styles.dateText}>{todayDisplay}</Text>
            {currentAccount && (
              <View style={styles.accountGoalContainer}>
                <Text style={styles.accountText}>{currentAccount.name}</Text>
                <Text style={[styles.goalNameText, { color: themeColor }]}>{mainGoal.name}</Text>
              </View>
            )}
          </View>

          <TouchableOpacity
            style={[styles.switchAccountButton, { backgroundColor: themeColor }]}
            onPress={() => navigation.navigate('JourneysOverview')}
          >
            <Text style={styles.switchAccountText}>All Journeys</Text>
          </TouchableOpacity>
        </View>

        {/* Main Goal Section */}
        <View style={styles.mainGoalSection}>
          <Text style={styles.sectionTitle}>Today's Main Goal</Text>

          <GoalCard
            goal={mainGoal}
            isCompleted={mainGoalCompleted}
            onToggleCompletion={handleToggleMainGoal}
            disabled={completionsLoading}
            themeColor={themeColor}
          />

          {/* Streak Info */}
          {hasLoadedCompletions ? (
            <View style={styles.streakContainer}>
              <View style={styles.streakItem}>
                <Text style={[styles.streakValue, { color: themeColor }]}>{mainGoalSummary.currentStreak}</Text>
                <Text style={styles.streakLabel}>Current Streak</Text>
              </View>
              <View style={styles.streakItem}>
                <Text style={[styles.streakValue, { color: themeColor }]}>{mainGoalSummary.previousStreak}</Text>
                <Text style={styles.streakLabel}>Previous Streak</Text>
              </View>
              <View style={styles.streakItem}>
                <Text style={[styles.streakValue, { color: themeColor }]}>{mainGoalSummary.daysSinceLastCompletion}</Text>
                <Text style={styles.streakLabel}>Days Since Last</Text>
              </View>
            </View>
          ) : (
            <View style={styles.streakContainer}>
              <ActivityIndicator size="small" color={themeColor} />
              <Text style={styles.loadingText}>Loading stats...</Text>
            </View>
          )}
        </View>

        {/* Side Goals Section */}
        {sideGoals.length > 0 && (
          <View style={styles.sideGoalsSection}>
            <TouchableOpacity
              style={styles.sideGoalsHeader}
              onPress={() => setShowSideGoals(!showSideGoals)}
            >
              <Text style={styles.sectionTitle}>Daily Actions ({sideGoals.length})</Text>
              <Text style={styles.toggleIcon}>{showSideGoals ? '▼' : '▶'}</Text>
            </TouchableOpacity>

            {showSideGoals &&
              sideGoals.map(goal => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  isCompleted={isCompletedToday(goal.id)}
                  onToggleCompletion={() => handleToggleSideGoal(goal.id)}
                  disabled={completionsLoading}
                  themeColor={themeColor}
                />
              ))}
          </View>
        )}

        {/* Add Side Goal Prompt (if no side goals yet) */}
        {sideGoals.length === 0 && (
          <View style={styles.addSideGoalPrompt}>
            <Text style={styles.addSideGoalPromptTitle}>Add Daily Actions</Text>
            <Text style={styles.addSideGoalPromptText}>
              Side goals are specific actions that support your main focus. Add them to track progress.
            </Text>
            <TouchableOpacity
              style={[styles.addSideGoalButton, { backgroundColor: themeColor }]}
              onPress={() => navigation.navigate('CreateMainGoal', { type: 'side' })}
            >
              <Text style={styles.addSideGoalButtonText}>+ Add Daily Action</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Quick Actions */}
        <View style={styles.actionsSection}>
          {sideGoals.length > 0 && (
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => navigation.navigate('CreateMainGoal', { type: 'side' })}
            >
              <Text style={[styles.actionButtonText, { color: themeColor }]}>+ Add Daily Action</Text>
            </TouchableOpacity>
          )}

          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => navigation.navigate('History', { goalId: mainGoal.id })}
          >
            <Text style={[styles.actionButtonText, { color: themeColor }]}>View History</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => navigation.navigate('Reconcile', { goalId: mainGoal.id })}
          >
            <Text style={[styles.actionButtonText, { color: themeColor }]}>Reconcile Days</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => navigation.navigate('GoalList')}
          >
            <Text style={[styles.actionButtonText, { color: themeColor }]}>Manage Goals</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => navigation.navigate('Settings')}
          >
            <Text style={[styles.actionButtonText, { color: themeColor }]}>Settings</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Encouragement Modal */}
      <EncouragementModal
        message={currentMessage}
        visible={currentMessage !== null}
        onDismiss={dismissMessage}
        onFeedback={submitFeedback}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  emptyTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
    textAlign: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 24,
  },
  emptyButton: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  emptyButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 32,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  headerInfo: {
    flex: 1,
  },
  dateText: {
    fontSize: 20,
    fontWeight: '600',
    color: '#333',
  },
  accountGoalContainer: {
    marginTop: 4,
  },
  accountText: {
    fontSize: 12,
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  goalNameText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#007AFF',
    marginTop: 2,
  },
  switchAccountButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#007AFF',
    borderRadius: 6,
  },
  switchAccountText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#fff',
  },
  mainGoalSection: {
    marginTop: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginHorizontal: 16,
    marginBottom: 8,
  },
  streakContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    backgroundColor: '#fff',
    marginHorizontal: 16,
    marginTop: 12,
    padding: 16,
    borderRadius: 12,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 3,
      },
      web: {
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      },
    }),
  },
  streakItem: {
    alignItems: 'center',
  },
  streakValue: {
    fontSize: 24,
    fontWeight: '700',
    color: '#007AFF',
  },
  streakLabel: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
  },
  sideGoalsSection: {
    marginTop: 24,
  },
  sideGoalsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginHorizontal: 16,
    marginBottom: 8,
  },
  toggleIcon: {
    fontSize: 16,
    color: '#666',
  },
  actionsSection: {
    marginTop: 24,
    marginHorizontal: 16,
  },
  actionButton: {
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 8,
    marginBottom: 12,
    alignItems: 'center',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 2,
      },
      android: {
        elevation: 2,
      },
      web: {
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
      },
    }),
  },
  actionButtonText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#007AFF',
  },
  primaryActionButton: {
    backgroundColor: '#007AFF',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.2,
        shadowRadius: 4,
      },
      android: {
        elevation: 4,
      },
      web: {
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
      },
    }),
  },
  primaryActionButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  addSideGoalPrompt: {
    marginTop: 24,
    marginHorizontal: 16,
    backgroundColor: '#F0F8FF',
    padding: 20,
    borderRadius: 12,
    alignItems: 'center',
  },
  addSideGoalPromptTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
    textAlign: 'center',
  },
  addSideGoalPromptText: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 20,
  },
  addSideGoalButton: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  addSideGoalButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
});
